import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { mongoId } from '../../../../@core/common/global';
import { ApiService } from '../../../../@core/service/api.service';
import { SharedDataService } from '../../../../@core/service/shared-data.service';
import { BaseProductModel } from '../../../../@models/erp/baseProduct';
import { CompilationModel } from '../../../../@models/erp/compilation';
import { Currency, IssuerData, UnitData } from '../../../../@models/erp/compilationData';
import { FinishedProductModel } from '../../../../@models/erp/finishedProduct';
import { PriceMatrixModel } from '../../../../@models/erp/priceMatrix';
import { EWizardAction, ProductWizardComponent, WizardAction } from '../../product-wizard/product-wizard.component';

@Component({
  selector: 'ngx-base-product-matrix',
  templateUrl: './base-product-matrix.component.html',
  styleUrls: ['./base-product-matrix.component.scss'],
})
export class BaseProductMatrixComponent implements OnChanges {

  @Input() fromWizard: ProductWizardComponent;
  @Input() baseProduct: BaseProductModel;
  @Input() finishedProducts: FinishedProductModel[];
  @Input() compilation: CompilationModel;
  @Input() issuer: number;
  @Input() fromOfferTemplate = false;
  @Input() newBaseProduct: BaseProductModel;
  @Output() wizardAction = new  EventEmitter<WizardAction>();

  issuerData = IssuerData;

  priceMatrixNrka: PriceMatrixModel[];
  priceMatrixNrkg: PriceMatrixModel[];

  unitOptions = UnitData.getShortOptions();
  currOptions = Currency.getOptions();

  rows: any[] = [];
  initialRows: any[] = [];
  sort: any = { col: '', dir: 1 }

  tableConfig = [
    // { id: 'issuer', name: 'Issuer' },
    { id: 'finishedProduct', name: 'Finished product'},
    { id: 'calcCode', name: 'product #'},
    { id: 'customer', name: 'Customer', header: 'Selling price', style: { 'border-left': '3px solid lightgray' } },
    { id: 'c-validFrom', name: 'Valid from', style: { 'width': '8.5em' }, edit: 'date' },
    { id: 'c-validTo', name: 'Valid to', style: { 'width': '8.5em' }, edit: 'date' },
    { id: 'c-type', name: 'Unit', style: { 'width': '4.5em', 'text-align': 'center' }, options: this.unitOptions },
    { id: 'c-quantity', name: 'Q', style: { 'width': '5em' }, edit: 'number', wizardOnly: true },
    { id: 'c-currency', name: 'Currency', style: { 'width': '6em', 'text-align': 'center' }, options: this.currOptions },
    { id: 'c-value', name: 'Price', style: { 'width': '5em' }, edit: 'number' },
    { id: 'c-minValue', name: 'min Price', style: { 'width': '5em', 'border-right': '3px solid lightgray' }, edit: 'number' },
    { id: 'supplier', name: 'Supplier', header: 'Buying price' },
    { id: 's-validFrom', name: 'Valid from', style: { 'width': '8.5em' }, edit: 'date' },
    { id: 's-validTo', name: 'Valid to', style: { 'width': '8.5em' }, edit: 'date' },
    { id: 's-type', name: 'Unit', style: { 'width': '4.5em', 'text-align': 'center' }, options: this.unitOptions },
    // { id: 's-quantity', name: 'Q', style: { 'width': '4em' }, edit: 'number' },
    { id: 's-currency', name: 'Currency', style: { 'width': '6em', 'text-align': 'center' }, options: this.currOptions },
    { id: 's-value', name: 'Price', style: { 'width': '5em' }, edit: 'number' },
    { id: 's-minValue', name: 'min Price', style: { 'width': '5em' }, edit: 'number' },
  ];

  constructor(private api: ApiService, private data: SharedDataService, private toast: NbToastrService ) { }


  ngOnChanges(changes: SimpleChanges): void {
    if (this.baseProduct && (changes.baseProduct || changes.issuer)) {
      if (this.newBaseProduct) {
        this.baseProduct.customer = this.newBaseProduct.customer && { checked: true, id: this.newBaseProduct.customer.id };
        this.baseProduct.supplier = this.newBaseProduct.supplier && { checked: true, id: this.newBaseProduct.supplier.id };
        this.prepareForNewBaseProduct();
      } else {
        this.api.loadBaseProductPriceMatrix(mongoId(this.baseProduct), this.issuer).then(table => {
          this.rows = table;
          this.initialRows = JSON.parse(JSON.stringify(table));
        })
        // this.prepareFinishedProducts();
      }
    }
  }

  gotoBaseProduct(item: BaseProductModel) {
    this.wizardAction.emit(new WizardAction(EWizardAction.showBaseProduct, item));
  }

  newFinishedProductWizard(item: BaseProductModel) {
    this.wizardAction.emit(new WizardAction(EWizardAction.newFinishedProduct, item));
  }

  gotoFinishedProduct(item: FinishedProductModel) {
    this.wizardAction.emit(new WizardAction(EWizardAction.showFinishedProduct, item));
  }

  async save() {
    const oldIdMap = {};

    if (this.newBaseProduct) {
      for (let i = 0; i < this.finishedProducts.length; i++) {
        const fp = this.finishedProducts[i];
        const row = this.rows[i];
        const oldId = fp._id;
        delete fp._id;
        fp.product = mongoId(this.newBaseProduct);
        if (row.checked) {
          fp.draft = false;
          const newFp = await this.api.saveFinishedProduct(fp);
          oldIdMap[oldId] = mongoId(newFp);
        }
      }
      this.newBaseProduct.draft = false;
      this.api.saveBaseProduct(this.newBaseProduct).then(() => {
        this.data.reloadProducts();
      })
    }

    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      const copy = this.initialRows[i];

      // selling price
      if (
        row['c-validFrom'] !== copy['c-validFrom']
        || row['c-type'] !== copy['c-type']
        || row['c-currency'] !== copy['c-currency']
        || row['c-value'] !== copy['c-value']
      ) {
        await this.api.savePrice(
          row.issuerId,
          mongoId(row.customerId),
          mongoId(this.newBaseProduct ? oldIdMap[row.finishedProductId] : row.finishedProductId),
          row['c-validFrom'],
          row['c-validTo'],
          row['c-currency'],
          0,
          row['c-type'],
          row['c-value'],
          row['c-minValue'],
        )
      }

      // buying price
      if (
        row['s-validFrom'] !== copy['s-validFrom']
        || row['s-type'] !== copy['s-type']
        || row['s-currency'] !== copy['s-currency']
        || row['s-value'] !== copy['s-value']
      ) {
        await this.api.savePrice(
          row.issuerId,
          mongoId(row.supplierId),
          mongoId(this.newBaseProduct ? oldIdMap[row.finishedProductId] : row.finishedProductId),
          row['s-validFrom'],
          row['s-validTo'],
          row['s-currency'],
          1,
          row['s-type'],
          row['s-value'],
          row['s-minValue'],
        )
      }

    }

    this.toast.success('Prices successfully saved', 'Price Matrix');
    if (this.fromOfferTemplate) {
      this.wizardAction.emit(new WizardAction(EWizardAction.close, {} ));
    }

  }

  genLineItemsFromFinishedProducts() {
    // this.wizardAction.emit(new WizardAction(EWizardAction.genLineItemsFromFinishedProducts, { fp: this.finishedProducts, rows: this.rows } ));
    const data = this.rows.filter(f => f['c-quantity']).map(m => ({ fp: this.finishedProducts.find(f => f._id === m['finishedProductId']), row: m }))

    this.wizardAction.emit(new WizardAction(EWizardAction.genLineItemsFromFinishedProducts, {
      fp: data.map(m => m.fp),
      rows: data.map(m => m.row)
    }));
  }

  rowChanged(line: any, col: any, idx: number) {
    if (/c-/.test(col.id) && !/c-validFrom/.test(col.id)) {
      if (line[col.id] !== this.initialRows[idx][col.id]) {
        line['c-validFrom'] = new Date();
        line['changed'] = true;
      }
    } else if (/s-/.test(col.id) && !/s-validFrom/.test(col.id)){
      if (line[col.id] !== this.initialRows[idx][col.id]) {
        line['s-validFrom'] = new Date();
        line['changed'] = true;
      }
    }
  }

  sortRows(col) {
    this.sort.col = col.id;
    this.sort.dir *= -1;
    this.rows = this.rows.sort((a, b) => a[this.sort.col] < b[this.sort.col] ? this.sort.dir : -this.sort.dir);

    // keep sort order in initial rows copy
    this.initialRows = this.rows.map(m => this.initialRows.find(f => f.finishedProductId === m.finishedProductId));
  }

  prepareForNewBaseProduct() {
    this.api.loadBaseProductPriceMatrix(mongoId(this.baseProduct), this.issuer).then(table => {
      // this.rows = table;
      // this.initialRows = JSON.parse(JSON.stringify(table));
      // console.log('loaded', this.rows, this.initialRows)

      this.rows = [];

      // this.api.loadBusiness(mongoId(this.fromWizard.customer)).subscribe(customer => {
        // this.api.loadBusiness(mongoId(this.fromWizard.supplier)).subscribe(supplier => {
          const customer = this.data.businesses.find(f => mongoId(f) === mongoId(this.fromWizard.customer));
          const supplier = this.data.businesses.find(f => mongoId(f) === mongoId(this.fromWizard.supplier));
          for (const fp of this.finishedProducts) {
            let row = table.find(t => t.finishedProductId === mongoId(fp));
            if (!row) row = {
              'issuerId': this.issuer,
              'finishedProductId': mongoId(fp),
              // 'customerId': this.fromWizard.customer,
              // 'supplierId': this.fromWizard.supplier,
              'issuer': IssuerData.get(this.issuer).name,
              // 'finishedProduct': (fp && `${fp.calcName} ${fp.calcCode}`) || '',
              'finishedProduct': (fp && fp.calcName) || '',
              'calcCode': (fp && fp.calcCode) || '',
              // 'customer': customer.name || '',
              'c-validFrom': '',
              'c-type': '',
              'c-quantity': '',
              'c-currency': '',
              'c-value': '',
              'c-minValue': '',
              // 'supplier': supplier.name || '',
              's-validFrom': '',
              's-type': '',
              's-quantity': '',
              's-currency': '',
              's-value': '',
              's-minValue': '',
            }
            row.checked = true;
            row.customerId = this.fromWizard.customer;
            row.supplierId = this.fromWizard.supplier;
            row.customer = customer.name || '';
            row.supplier = supplier.name || '';
            this.rows.push(row);
          }

          this.initialRows = JSON.parse(JSON.stringify(this.rows));
          this.evalProducts();
        })
      // });
    // })
  }

  prepareFinishedProducts() {
    this.api.loadBaseProductPriceMatrix(mongoId(this.baseProduct), this.issuer).then(table => {
      this.rows = [];
      for (const fp of this.finishedProducts) {
        for (const customerId of fp.customers.filter(f => f.checked).map(m => m.id)) {
          for (const supplierId of fp.suppliers.filter(f => f.checked).map(m => m.id)) {

            let row = table.find(t =>
              t.finishedProductId === mongoId(fp)
              && t.customerId === customerId
              && t.supplierId === supplierId
            );
            if (!row) {
              const customer = this.data.businesses.find(b => mongoId(b) === mongoId(customerId));
              const supplier = this.data.businesses.find(b => mongoId(b) === mongoId(supplierId));
              row = {
                'issuerId': this.issuer,
                'finishedProductId': mongoId(fp),
                'customerId': customerId,
                'supplierId': supplierId,
                'issuer': IssuerData.get(this.issuer).name,
                // 'finishedProduct': (fp && `${fp.calcName} ${fp.calcCode}`) || '',
                'finishedProduct': (fp && fp.calcName) || '',
                'calcCode': (fp && fp.calcCode) || '',
                'customer': customer.name || '',
                'c-validFrom': '',
                'c-type': '',
                'c-quantity': '',
                'c-currency': '',
                'c-value': '',
                'c-minValue': '',
                'supplier': supplier.name || '',
                's-validFrom': '',
                's-type': '',
                's-quantity': '',
                's-currency': '',
                's-value': '',
                's-minValue': '',
              }
            }
            this.rows.push(row);
          }
        }
      }

      this.initialRows = JSON.parse(JSON.stringify(this.rows));
      this.evalProducts();
    });
  }

  itemsChanged() {
    this.evalProducts();
  }

  evalProducts() {
    let ct = 0;
    // this.finishedProducts.filter(fp => (this.rows.find(r => r.finishedProductId === mongoId(fp)) || {}).checked).forEach(fp => {
    //   delete fp._id;
    //   const code = this.data.finishedProducts.length ?
    //     '' + (1 + (ct++) + this.data.finishedProducts.map(m => +m.code || 0).reduce((a, b) => Math.max(a, b))) : '1';
    //   fp.code = [0,0,0,0,0,0,0,0,0].splice(code.length).join('') + code;
    //   const comp = new FinishedProductComponent(this.api, null, null, null, null, null);
    //   comp.baseProducts = this.data.baseProducts;
    //   comp.businessUnits = this.data.businessUnits;
    //   comp.getCalcCode(fp);
    //   console.log('fp code', code, fp, this.finishedProducts, this.rows)
    // });

    for (let i = 0; i < this.finishedProducts.length; i++) {
      const fp = this.finishedProducts[i];
      const row = this.rows[i];
      if (!row.checked && this.fromOfferTemplate) {
        row.calcCode = 'n/a';
        continue;
      }
      const code = this.data.finishedProducts.length ?
        '' + (1 + (ct++) + this.data.finishedProducts.map(m => +m.code || 0).reduce((a, b) => Math.max(a, b))) : '1';
      fp.code = [0,0,0,0,0,0,0,0,0].splice(code.length).join('') + code;
      // const comp = new FinishedProductComponent(this.api, null, null, null, null, null);
      // comp.baseProducts = this.data.baseProducts;
      // comp.businessUnits = this.data.businessUnits;
      // comp.getCalcCode(fp);
      fp.calcCode = fp.calcCode.replace(/\d+$/, code);
      row.calcCode = fp.calcCode;
      // console.log('fp code', code, fp.calcCode, this.finishedProducts, row, this.rows)
    }

    // this.rows.filter(row => row && row.checked).forEach(row => {

    //   const fp = this.data.finishedProducts.find(f => mongoId(f) === mongoId(row.finishedProductId));
    //   console.log('fp', fp, row.finishedProductId);
    //   if (!fp) return;
    //   const code = this.data.finishedProducts.length ?
    //     '' + (1 + (ct++) + this.data.finishedProducts.map(m => +m.code || 0).reduce((a, b) => Math.max(a, b))) : '1';
    //   fp.code = [0,0,0,0,0,0,0,0,0].splice(code.length).join('') + code;
    //   const comp = new FinishedProductComponent(this.api, null, null, null, null, null);
    //   comp.baseProducts = this.data.baseProducts;
    //   comp.businessUnits = this.data.businessUnits;
    //   comp.getCalcCode(fp);
    //   row.calcCode = fp.calcCode;
    //   console.log('fp code', code, fp.calcCode, this.finishedProducts, row, this.rows)

    // })

  }

}
